import { mapLocationsDtoToLocations } from 'modules/locations/domain/data-mappers/location-data-mapper';
import { LocationResponseDto } from 'modules/locations/domain/interfaces/response-dto';
import { Location } from 'modules/locations/domain/interfaces/location';
import { baseApi } from 'shared/api/base-api';

/** Тег перечня локаций: по нему мутации сбрасывают именно список, а не карточки. */
export const LOCATIONS_LIST_TAG = { type: 'Location' as const, id: 'PARTIAL-LIST' };

/**
 * Список локаций для страницы `/locations`. Запрос повторяет
 * `locationsRepository.fetchLocations`, но живёт в RTK Query: пагинация страницы
 * построена на `usePagedList`, которому нужны `data`/`currentData` и инвалидация
 * по тегам, а не накопительный слайс.
 *
 * Выборка урезана до того, что рисует строка: карту (`mapImage`) и описание
 * читает карточка локации, списку они не нужны.
 */
const LIST_LOCATIONS_QUERY = `#graphql
  query getTournamentLocations($tournamentId: Int!, $limit: Int!, $offset: Int!, $nameFilter: String) {
    tournament(id: $tournamentId) {
      id
      locations(limit: $limit, offset: $offset, nameFilter: $nameFilter) {
        id
        name
        address {
          id
          addressLine1
          addressLine2
          city
          region
          country
          coords {
            lat
            lon
          }
        }
        locationImages {
          image {
            id
            filename
            externalUrl
          }
          ordinalNumber
        }
        tournament {
          id
        }
      }
    }
  }
`;

export interface ListLocationsArgs {
  tournamentId: number;
  limit: number;
  offset: number;
  nameFilter?: string;
}

interface ListLocationsResponseDto {
  tournament?: {
    id: number;
    locations?: LocationResponseDto[];
  };
}

/** Экспортируется для юнит-тестов: пустой ответ должен давать пустой список, а не падать. */
export function mapListLocationsResponse(raw: ListLocationsResponseDto): Location[] {
  return mapLocationsDtoToLocations(raw?.tournament?.locations ?? []);
}

export const locationsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listLocations: build.query<Location[], ListLocationsArgs>({
      query: ({ tournamentId, limit, offset, nameFilter }) => ({
        query: LIST_LOCATIONS_QUERY,
        variables: { tournamentId, limit, offset, nameFilter },
      }),
      transformResponse: mapListLocationsResponse,
      // Идентификаторы локаций рядом с тегом списка: правка локации обновляет её
      // строку в перечне, а создание и удаление бьют по тегу списка.
      providesTags: (result) => [
        LOCATIONS_LIST_TAG,
        ...(result ?? []).map((location) => ({ type: 'Location' as const, id: location.id })),
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useListLocationsQuery } = locationsApi;
